import { Injectable } from '@angular/core';
import { SmsJourneyStep } from '@app/core/enums/sms-journey-step.enum';
import { Progress } from '@app/core/models/payment/progress.interface';
import { BehaviorSubject, Observable } from 'rxjs';
import { map } from 'rxjs/operators';

@Injectable({
  providedIn: 'root'
})
export class PendingPlanProgressService {
  private progressSubject = new BehaviorSubject<Progress[]>([]);

  private excludedFromSmsJourney = [SmsJourneyStep.SaveAsDraft, SmsJourneyStep.SmsSent];

  setProgress(progress: Progress[]): void {
    this.progressSubject.next(progress);
  }

  getProgress(): Progress[] {
    return this.progressSubject.getValue();
  }

  getSmsJourneySteps$(): Observable<Progress[]> {
    return this.progressSubject.pipe(
      map(progress =>
        progress.filter(
          step =>
            !this.excludedFromSmsJourney.includes(step.custrecord_spps_step_id as SmsJourneyStep) &&
            !step.isHidden
        )
      )
    );
  }

  getSmsJourneySteps(): Progress[] {
    return this.getProgress().filter(
      step =>
        !this.excludedFromSmsJourney.includes(step.custrecord_spps_step_id as SmsJourneyStep) &&
        !step.isHidden
    );
  }

  updateStepByInternalId(stepInternalId: string, newValue: Partial<Progress>): void {
    const newProgress = this.getProgress().map(step => {
      if (step.internalid === stepInternalId) {
        return {
          ...step,
          ...newValue
        };
      }
      return step;
    });
    this.setProgress(newProgress);
  }

  updateStepByStepId(stepId: SmsJourneyStep, newValue: Partial<Progress>): void {
    const newProgress = this.getProgress().map(step => {
      if (step.custrecord_spps_step_id === stepId) {
        return {
          ...step,
          ...newValue
        };
      }
      return step;
    });
    this.setProgress(newProgress);
  }

  getStepByStepId(step: SmsJourneyStep): Progress | undefined {
    return this.getProgress().find(progress => progress.custrecord_spps_step_id === step);
  }

  getStepByInternalId(internalid: string): Progress | undefined {
    return this.getProgress().find(progress => progress.internalid === internalid);
  }

  isLastStep(step: SmsJourneyStep): boolean {
    const lastStep = [...this.getProgress()].pop();
    return lastStep?.custrecord_spps_step_id === step || false;
  }

  getPreviousStepByStepId(step: SmsJourneyStep): SmsJourneyStep | null {
    const stepIndex = this.getProgress().findIndex(_step => _step.custrecord_spps_step_id === step);
    const previousStep = this.getProgress()[stepIndex - 1];

    return previousStep ? (previousStep.custrecord_spps_step_id as SmsJourneyStep) : null;
  }

  getNextStepByStepId(step: SmsJourneyStep): SmsJourneyStep | null {
    const stepIndex = this.getProgress().findIndex(_step => _step.custrecord_spps_step_id === step);
    const nextStep = this.getProgress()[stepIndex + 1];

    return nextStep ? (nextStep.custrecord_spps_step_id as SmsJourneyStep) : null;
  }

  getNextIncompleteStepId(): SmsJourneyStep | null {
    const steps = this.getProgress();
    const nextIncompleteStep = steps.find(step => !step.custrecord_spps_completed)
      ?.custrecord_spps_step_id as SmsJourneyStep;
    return nextIncompleteStep || null;
  }

  getRouteByStepId(stepId: SmsJourneyStep): string {
    switch (stepId) {
      case SmsJourneyStep.TreatmentQuote:
        return 'treatment-proposal/quote';
      case SmsJourneyStep.TreatmentSelection:
        return 'treatment-proposal/options';
      case SmsJourneyStep.TreatmentPaymentFlowSelection:
        return 'treatment-proposal/select-payment';
      case SmsJourneyStep.PracticeDocument:
        return 'treatment-proposal/documents';
      default:
        return '/payment-sms-journey';
    }
  }

  isStepComplete$(stepId: SmsJourneyStep): Observable<boolean> {
    return this.progressSubject.pipe(
      map(progress => {
        const step = progress.find(p => p.custrecord_spps_step_id === stepId);
        return step?.custrecord_spps_completed || false;
      })
    );
  }
}
